import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface DeloreanLoaderProps {
  width?: number;
  height?: number;
}

export default function DeloreanLoader({ width, height = 100 }: DeloreanLoaderProps) {
  const theme = useTheme();

  // Get colors from theme
  const strokeColor = theme.palette.mode === 'dark' ? '#fff' : '#000';
  const fillColor = theme.palette.mode === 'dark' ? '#001a00' : '#fff';
  const accentColor = theme.palette.primary.main || '#41aade';

  // Generate unique ID for this instance to avoid CSS conflicts
  const instanceId = React.useId().replace(/:/g, '');

  const styles = `
    @keyframes iconEnter-${instanceId} {
      0% { transform: translateY(-50%) translateX(110%) scaleX(1); }
      100% { transform: translateY(-50%) translateX(-110%) scaleX(1); }
    }

    @keyframes iconLoop-${instanceId} {
      0% { transform: translateY(-50%) translateX(-110%) scaleX(1); }
      49% { transform: translateY(-50%) translateX(110%) scaleX(1); }
      50% { transform: translateY(-50%) translateX(110%) scaleX(-1); }
      99% { transform: translateY(-50%) translateX(-110%) scaleX(-1); }
      100% { transform: translateY(-50%) translateX(-110%) scaleX(1); }
    }

    @keyframes wheelsMove-${instanceId} {
      0% { transform: translateY(0px) rotate(0deg); }
      30% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-2px) rotate(0.5deg); }
      60% { transform: translateY(0px) rotate(0deg); }
      70% { transform: translateY(-2px) rotate(-0.5deg); }
      90% { transform: translateY(0px) rotate(0deg); }
      100% { transform: translateY(0px) rotate(0deg); }
    }

    @keyframes bodyMove-${instanceId} {
      0% { transform: translate(0px, 0px) rotate(0deg); }
      30% { transform: translate(0px, 2px) rotate(0deg); }
      50% { transform: translate(0px, 0px) rotate(1deg); }
      70% { transform: translate(0px, -1px) rotate(0deg); }
      90% { transform: translate(0px, 0px) rotate(-1deg); }
      100% { transform: translate(0px, 0px) rotate(0deg); }
    }

    @keyframes poleMove-${instanceId} {
      0% { transform: rotate(0deg); }
      30% { transform: rotate(0deg); }
      50% { transform: rotate(0deg); }
      70% { transform: rotate(1deg); }
      90% { transform: rotate(-2deg); }
      100% { transform: rotate(0deg); }
    }

    @keyframes stripesAnimate-${instanceId} {
      0% {
        stroke-dasharray: 0, 100, 0, 100;
        stroke-dashoffset: -300;
        stroke-width: 1;
      }
      25% {
        stroke-dasharray: 100, 80, 10, 50;
        stroke-dashoffset: -150;
        stroke-width: 1;
      }
      50% {
        stroke-dasharray: 100, 20, 20, 50;
        stroke-dashoffset: 0;
        stroke-width: 3;
      }
      75% {
        stroke-dasharray: 100, 80, 40, 50;
        stroke-dashoffset: 150;
        stroke-width: 1;
      }
      100% {
        stroke-dasharray: 0, 100, 0, 100;
        stroke-dashoffset: 300;
        stroke-width: 1;
      }
    }

    @keyframes stripesAnimateOrange-${instanceId} {
      0% {
        stroke-dasharray: 300, 50, 50, 50;
        stroke-dashoffset: -600;
        stroke-width: 2;
        opacity: 0;
      }
      10% {
        stroke-dasharray: 350, 30, 80, 30;
        stroke-dashoffset: -400;
        stroke-width: 4;
        opacity: 1;
      }
      25% {
        stroke-dasharray: 400, 20, 100, 20;
        stroke-dashoffset: -200;
        stroke-width: 6;
        opacity: 1;
      }
      50% {
        stroke-dasharray: 350, 30, 80, 30;
        stroke-dashoffset: 100;
        stroke-width: 4;
        opacity: 0.8;
      }
      100% {
        stroke-dasharray: 300, 50, 50, 50;
        stroke-dashoffset: 600;
        stroke-width: 2;
        opacity: 0;
      }
    }

    .delorean-container-${instanceId} {
      position: relative;
      overflow: hidden;
      width: 100%;
      height: 100%;
    }

    .delorean-icon-${instanceId} {
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 85%;
      width: auto;
      transform: translateY(-50%) translateX(110%) scaleX(1);
      animation: 
        iconEnter-${instanceId} 1.5s cubic-bezier(.5, 0, 0.2, 1) forwards,
        iconLoop-${instanceId} 10s linear 1.5s infinite;
    }

    .delorean-icon-${instanceId} svg {
      height: 100%;
      width: auto;
      transform: scaleX(-1);
      display: block;
    }

    .delorean-base-${instanceId} {
      fill: none;
      stroke: ${strokeColor};
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-miterlimit: 10;
    }

    .delorean-stroke-filled-${instanceId} {
      fill: ${fillColor};
    }

    .delorean-accent-${instanceId} {
      stroke: ${accentColor};
    }

    .delorean-stroke-bold-${instanceId} { stroke-width: 7; }
    .delorean-stroke-normal-${instanceId} { stroke-width: 5; }
    .delorean-stroke-light-${instanceId} { stroke-width: 4; }
    .delorean-stroke-lighter-${instanceId} { stroke-width: 3; }

    .delorean-body-${instanceId} {
      animation: bodyMove-${instanceId} 0.5s linear infinite;
      transform-origin: 35% 65%;
    }

    .delorean-wheels-${instanceId} {
      animation: wheelsMove-${instanceId} 0.5s linear infinite;
      transform-origin: 50% 50%;
    }

    .delorean-pole-${instanceId} {
      animation: poleMove-${instanceId} 0.5s linear infinite;
      transform-origin: 51% 37%;
    }

    .delorean-stripe-${instanceId} {
      stroke: ${strokeColor};
      stroke-width: 0;
      animation: stripesAnimate-${instanceId} 1s linear infinite;
    }

    .delorean-stripe-orange-${instanceId} {
      stroke: #ff6600;
      stroke-width: 0;
      animation: stripesAnimateOrange-${instanceId} 4s ease-out infinite;
    }

    .delorean-stripe-${instanceId}:nth-child(2) {
      animation-delay: 0.5s;
    }

    .delorean-stripe-${instanceId}:nth-child(4) {;
    }

    .delorean-stripe-${instanceId}:nth-child(4) {
      animation-delay: 0.25s;
    }

    .delorean-glow-${instanceId} {
      stroke-width: 0;
    }
  `;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: width || '100%',
        height: height,
        overflow: 'hidden',
      }}
    >
      <style>{styles}</style>
      <div className={`delorean-container-${instanceId}`}>
        <div className={`delorean-icon-${instanceId}`}>
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 600 240"
          >
            <g className="car">
              {/* Body */}
              <g className={`delorean-body-${instanceId}`}>
                {/* Fin */}
                <polygon
                  className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId} delorean-stroke-filled-${instanceId}`}
                  points="357.4,99.2 399.9,74.9 407.1,95.3 406,113.3 402.9,116.8 404.6,129.2 409.8,134.5 408,145.9 403.4,146.1 393.6,151.7"
                />

                {/* Body outline */}
                <path
                  className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId} delorean-stroke-filled-${instanceId}`}
                  d="M151.6,166h9.9l-2.4,7.3h143.1l-5-9.5H309h51.4l32.3-9.2c2.9-8.7,5-30.5-1.1-47.5l-89.3-20.7h-69.8 l-55.2,22.3l-81,10.8l-30.9,11.3l-3.2,9.7l-6,6.7l0.8,6.2h7.7l11.3,6.2h24.5L151.6,166z"
                />

                {/* Door */}
                <polyline
                  className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`}
                  points="177.3,108.7 189.8,163.2 256.1,163.2 291.6,121.9 291.6,86.4"
                />

                {/* Back windshield */}
                <polyline
                  className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`}
                  points="302.4,86.4 302.4,114.5 373.6,114.5 370.1,102.1"
                />
                <polyline
                  className={`delorean-base-${instanceId} delorean-stroke-light-${instanceId}`}
                  points="317.1,89.8 317.1,105.1 323.3,114.5"
                />
                <polyline
                  className={`delorean-base-${instanceId} delorean-stroke-light-${instanceId}`}
                  points="328.7,92.5 328.7,105.1 334.9,114.5"
                />
                <polyline
                  className={`delorean-base-${instanceId} delorean-stroke-light-${instanceId}`}
                  points="339.4,95 339.4,105.1 345.6,114.5"
                />

                {/* Top part */}
                <line className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`} x1="285.3" y1="82.7" x2="298" y2="82.7" />
                <line className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`} x1="292.6" y1="76.6" x2="292.6" y2="82.7" />
                <line className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`} x1="283.2" y1="75.9" x2="299.4" y2="77.3" />

                {/* Window */}
                <polygon
                  className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`}
                  points="282.3,96.1 282.3,118.1 192.2,120.7 236.8,96.1"
                />
                <polyline
                  className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`}
                  points="217.5,120 239.8,106.3 263.7,106.3 270.6,118.4"
                />

                {/* Body stroke */}
                <line className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`} x1="184.6" y1="140.5" x2="62.2" y2="140.5" />
                <line className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`} x1="184.1" y1="138.5" x2="277.3" y2="138.5" />
                <line className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`} x1="395.3" y1="140.5" x2="275.5" y2="140.5" />

                {/* Other details */}
                <line className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`} x1="274.6" y1="126.5" x2="274.6" y2="126.5" />
                <line className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`} x1="253.8" y1="144.5" x2="262.1" y2="144.5" />
                <line className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`} x1="84.5" y1="147.6" x2="91.1" y2="147.6" />
                <line className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`} x1="78.1" y1="146.8" x2="78.1" y2="150.7" />

                {/* Pole */}
                <g className={`delorean-pole-${instanceId}`}>
                  <line className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`} x1="307.9" y1="87.7" x2="365.3" y2="30.3" />
                  <path
                    className={`delorean-base-${instanceId} delorean-stroke-lighter-${instanceId}`}
                    d="M365.3,30.3l4.9-4.9c0.9-0.9,2.4-1,3.4-0.2l1.3,1c1.5,1.3,3.8,1.1,5.1-0.3l6.1-6.5c2.1-2.2,1.9-5.7-0.4-7.7 l-1.7-1.4c-2.5-2.1-6.2-2-8.5,0.2l-0.2,0.2"
                  />
                </g>
              </g>

              {/* Wheels */}
              <g className={`delorean-wheels-${instanceId}`}>
                <g className="wheel">
                  <circle className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId} delorean-stroke-filled-${instanceId}`} cx="126.3" cy="160.7" r="25.9" />
                  <circle className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`} cx="126.3" cy="160.5" r="16" />
                </g>
                <g className="wheel">
                  <circle className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId} delorean-stroke-filled-${instanceId}`} cx="334.8" cy="160.7" r="25.9" />
                  <circle className={`delorean-base-${instanceId} delorean-stroke-normal-${instanceId}`} cx="334.8" cy="160.5" r="16" />
                </g>
              </g>
            </g>

            {/* Stripes */}
            <g className="stripes">
              <line className={`delorean-stripe-${instanceId}`} x1="587.6" y1="168.6" x2="370.7" y2="168.6" />
              <line className={`delorean-stripe-${instanceId}`} x1="533.3" y1="151.7" x2="443.2" y2="151.7" />
              <line className={`delorean-stripe-orange-${instanceId}`} x1="750" y1="176.5" x2="359.9" y2="176.5" />
              <line className={`delorean-stripe-${instanceId}`} x1="472.3" y1="138.5" x2="425" y2="138.5" />
            </g>

            {/* Glows */}
            <g className="glows">
              <path className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} d="M42.7,130.4L42.7,130.4L42.7,130.4c0-40,32.4-72.4,72.4-72.4h62.4" />
              <path className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} d="M42.7,130.4L42.7,130.4L42.7,130.4c0-26.6,21.6-48.2,48.2-48.2h14.6" />
              <path className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} d="M42.6,130.5L42.6,130.5L42.6,130.5c0-11.1,9-20.1,20.1-20.1h92.7" />
              <path className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} d="M42.6,130.4L42.6,130.4L42.6,130.4c0,11.1,9,20.1,20.1,20.1h22.7" />
              <path className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} d="M42.7,130.4L42.7,130.4L42.7,130.4c0,40,32.4,72.4,72.4,72.4h82.4" />
              <path className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} d="M42.7,130.4L42.7,130.4L42.7,130.4c0,26.6,21.6,48.2,48.2,48.2h94.6" />
              <line className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} x1="174.5" y1="165.4" x2="297" y2="165.4" />
              <line className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} x1="175.9" y1="193.8" x2="332.8" y2="193.8" />
              <line className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} x1="114.7" y1="66.4" x2="292.5" y2="66.4" />
              <line className={`delorean-glow-${instanceId} delorean-accent-${instanceId}`} x1="184.3" y1="119" x2="379.9" y2="119" />
            </g>
          </svg>
        </div>
      </div>
    </Box>
  );
}
